import React from "react";
import {
  ByTenantStats,
  ByUserStats,
} from "../../../types/form-generation-usage.types";

interface BreakdownTableProps<T> {
  title: string;
  data: T[];
  nameKey: keyof T;
  keyField: keyof T;
  testId: string;
}

function BreakdownTable<T extends { count: number }>({
  title,
  data,
  nameKey,
  keyField,
  testId,
}: BreakdownTableProps<T>) {
  const total = data.reduce((sum, item) => sum + item.count, 0);

  if (!data.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-4">
        <h4 className="text-sm font-semibold text-neutral-900 mb-3">{title}</h4>
        <div className="text-neutral-500 text-sm">Brak danych</div>
      </div>
    );
  }

  // Sort by count descending
  const sortedData = [...data].sort((a, b) => b.count - a.count);

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-4"
      data-testid={testId}
    >
      <h4 className="text-sm font-semibold text-neutral-900 mb-3">{title}</h4>

      <div className="space-y-3">
        {sortedData.map((item, index) => {
          const percentage = total > 0 ? (item.count / total) * 100 : 0;
          const name = String(item[nameKey]);

          return (
            <div key={String(item[keyField])} data-testid={`${testId}-item-${index}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-neutral-700 truncate mr-2">{name}</span>
                <span className="text-neutral-900 font-medium flex-shrink-0">
                  {item.count.toLocaleString("pl-PL")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all"
                    style={{ width: `${percentage}%` }}
                    role="progressbar"
                    aria-valuenow={percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${name}: ${percentage.toFixed(1)}% generacji`}
                  />
                </div>
                <span className="text-xs text-neutral-500 w-12 text-right">
                  {percentage.toFixed(1)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-neutral-100">
        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 font-medium">Suma</span>
          <span className="text-neutral-900 font-semibold">
            {total.toLocaleString("pl-PL")}
          </span>
        </div>
      </div>
    </div>
  );
}

interface BreakdownTablesProps {
  byTenant: ByTenantStats[];
  byUser: ByUserStats[];
}

export const BreakdownTables: React.FC<BreakdownTablesProps> = ({
  byTenant,
  byUser,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <BreakdownTable<ByTenantStats>
        title="Wg organizacji"
        data={byTenant}
        nameKey="tenantName"
        keyField="tenantId"
        testId="chart-by-tenant"
      />
      <BreakdownTable<ByUserStats>
        title="Wg użytkownika"
        data={byUser}
        nameKey="userName"
        keyField="userId"
        testId="chart-by-user"
      />
    </div>
  );
};

export default BreakdownTables;
